// "Memoria descriptiva" del día para el Calendario (2 a 4 oraciones), armada con plantillas a partir de los números:
// personas y horas (YOLOX, como siempre), entregas / mixers / máquinas / materiales (iaV3, RF-DETR) y clima.
// Compara contra el día trabajado anterior. Determinística: no llama a ninguna IA. Si el usuario abre el día,
// CalendarioObra la manda a pulir con Claude Haiku (pulirMemoria, con caché en el servidor).
import { fmtHM, arHour } from './iaData';
import { MATERIAL_LABEL, textoMateriales } from './entregasV3';

const DOW = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const dowOf = (k) => DOW[new Date(k + 'T12:00:00Z').getUTCDay()];
const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;
const lista = (xs) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}`);
const cap = (t) => t.charAt(0).toUpperCase() + t.slice(1);
const VEH = { Camion: ['camión', 'camiones'], 'Camion volquete': ['camión volquete', 'camiones volquete'], Camioneta: ['camioneta', 'camionetas'] };

const lugar = (m) => {
  const x = m.cx < 0.33 ? 'a la izquierda' : m.cx > 0.66 ? 'a la derecha' : 'en el centro';
  return m.cy < 0.4 ? `${x}, al fondo` : x;
};

/** Día trabajado anterior a k (con personas), o null. */
export function diaAnterior(k, sums) {
  const ks = [...sums.keys()].filter((x) => x < k && sums.get(x).first != null).sort();
  return ks.length ? ks[ks.length - 1] : null;
}

/**
 * k: día; s: resumen del día (daySummary de entregas.js, con visits ya filtradas); prevK/prev: día trabajado anterior;
 * wx: clima crudo del día ({p, max, min}); res / resPrev: resumen iaV3 del día y del anterior (materiales y máquinas);
 * marks: lo que marcó el cliente (para no contar las entregas descartadas).
 * Devuelve { texto, datos } (datos = lo mismo en números, para que Haiku no invente).
 */
export function memoriaDia({ k, s, prevK, prev, wx, res, resPrev, marks = {} }) {
  const fr = [];
  const datos = { dia: k, diaAnterior: prevK || null };
  const ref = prevK ? `el ${dowOf(prevK)}` : null;

  // 1. Jornada y gente (YOLOX)
  if (s.first != null) {
    let t = `Jornada de ${fmtHM(s.first)} a ${fmtHM(s.last)} con pico de ${plural(s.peak, 'persona', 'personas')}`;
    if (prev && prev.peak != null) {
      const d = s.peak - prev.peak;
      t += d === 0 ? ` (igual que ${ref})` : ` (${Math.abs(d)} ${d > 0 ? 'más' : 'menos'} que ${ref})`;
    }
    fr.push(t + '.');
    Object.assign(datos, { entrada: fmtHM(s.first), salida: fmtHM(s.last), picoPersonas: s.peak, picoAnterior: prev ? prev.peak : null });
  } else {
    fr.push('No se vieron personas trabajando en la obra.');
  }

  // 2. Entregas, hormigonado y máquinas (iaV3; con la heurística vieja solo camiones)
  const vis = (s.visits || []).filter((v) => marks[v.id]?.estado !== 'no');
  const camiones = vis.filter((v) => v.tipo !== 'maquina' && v.tipo !== 'material' && v.cls !== 'Mixer');
  const mixers = vis.filter((v) => v.cls === 'Mixer');
  const maquinas = vis.filter((v) => v.tipo === 'maquina');
  const sueltos = vis.filter((v) => v.tipo === 'material');
  const bits = [];
  if (camiones.length) {
    const tipos = {};
    camiones.forEach((v) => { tipos[v.cls] = (tipos[v.cls] || 0) + 1; });
    const quien = lista(Object.entries(tipos).map(([c, n]) => (n === 1 ? `un ${(VEH[c] || [c])[0]}` : `${n} ${(VEH[c] || [c, c])[1]}`)));
    const mats = textoMateriales(camiones.flatMap((v) => [...(v.materiales || []), ...(v.carga || [])]));
    const man = camiones.filter((v) => arHour(v.arrive) < 12).length;
    const cuando = camiones.length === 1 ? `a las ${fmtHM(camiones[0].arrive)}` : man === camiones.length ? 'por la mañana' : man === 0 ? 'por la tarde' : 'durante el día';
    bits.push(`${camiones.length === 1 ? 'llegó' : 'llegaron'} ${quien}${mats ? ` con ${mats}` : ''} ${cuando}`);
  }
  const horm = mixers.filter((v) => v.hormigonado);
  if (horm.length) {
    const a = Math.min(...horm.map((v) => v.arrive)), b = Math.max(...horm.map((v) => v.leave));
    const min = Math.max(20, Math.round((b - a) / 60e3) + 20);
    bits.push(`hubo hormigonado probable de ${fmtHM(a)} a ${fmtHM(b)} (mixer ${min >= 90 ? `${Math.round(min / 60)} h` : `${min} min`})`);
  } else if (mixers.length) bits.push(`pasó un mixer a las ${fmtHM(mixers[0].arrive)}`);
  if (maquinas.length) bits.push(`entró una ${maquinas[0].cls === 'Minicargadora' ? 'minicargadora' : 'excavadora'} a las ${fmtHM(maquinas[0].arrive)}`);
  if (sueltos.length) bits.push(`aparecieron ${textoMateriales(sueltos.flatMap((v) => v.materiales))} nuevos`);
  if (bits.length) fr.push(cap(lista(bits)) + '.');
  else if (s.fuente === 'iaV3') fr.push('No se vieron entregas de material.');
  datos.entregas = vis.map((v) => ({ hora: fmtHM(v.arrive), vehiculo: v.cls, materiales: (v.materiales || []).map((m) => MATERIAL_LABEL[m.cls]), hormigonado: !!v.hormigonado }));

  // 3. Materiales a la vista contra el día anterior (iaV3: área de las siluetas, mediana del día)
  if (res && resPrev && ref) {
    const cambios = [];
    for (const [cls, m] of Object.entries(res.materiales || {})) {
      if (cls === 'pila_arena_tierra') continue; // en excavaciones cambia todo el tiempo
      const p = (resPrev.materiales || {})[cls];
      const a0 = p ? p.area : 0;
      if (m.area - a0 >= 0.003 && m.area >= 1.5 * a0) cambios.push({ cls, txt: `más ${MATERIAL_LABEL[cls]} ${lugar(m)}`, d: m.area - a0 });
    }
    for (const [cls, p] of Object.entries(resPrev.materiales || {})) {
      if (cls === 'pila_arena_tierra') continue;
      const a1 = ((res.materiales || {})[cls] || { area: 0 }).area;
      if (p.area - a1 >= 0.003 && a1 <= 0.6 * p.area) cambios.push({ cls, txt: `menos ${MATERIAL_LABEL[cls]} ${lugar(p)}`, d: p.area - a1 });
    }
    cambios.sort((a, b) => b.d - a.d);
    if (cambios.length) fr.push(`Se ven ${lista(cambios.slice(0, 2).map((c) => c.txt))} que ${ref}.`);
    datos.materialesCambio = cambios.slice(0, 3).map((c) => c.txt);
  }

  // 4. Clima
  if (wx) {
    if (wx.p >= 1) fr.push(`Llovió ${String(+wx.p.toFixed(1)).replace('.', ',')} mm.`);
    else if (fr.length < 4 && wx.max != null) fr.push(`Sin lluvia, máxima de ${Math.round(wx.max)}°.`);
    datos.lluviaMm = wx.p; datos.max = wx.max;
  }
  return { texto: fr.slice(0, 4).join(' '), datos };
}
